import type { IProfileMetrics, IProfileMetricsV2 } from '~/types/wpnStore'

type ProfileMetricsResponse = {
  ids: string[]
  metrics: { [key: string]: IProfileMetrics | IProfileMetricsV2 | null }
}

interface UseTestProfileMetricsOptions {
  // Skip the live metrics API when this returns true (mock overlay still applies).
  skipLiveFetch?: () => boolean
}

// Shared profile-metrics fetch. Hits the live API and overlays seeded V2 mock
// data (incl. store tier) for stores in the parent page's testDataStoreIds.
export async function useTestProfileMetrics(
  options: UseTestProfileMetricsOptions = {},
) {
  const route = useRoute()
  const userStore = useUserStore()
  const { stores, roles } = storeToRefs(userStore)

  const testDataStoreIds = await useTestDataStoreIds()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ufetch = $fetch as typeof $fetch<any>

  // Numeric, locale-independent data: no locale in the key or watcher, so
  // switching languages doesn't refetch (or momentarily blank) the graphs.
  const { data } = await useAsyncData(
    `new-profile-metrics-data-${route.params?.slug}`,
    async () => {
      const allIds = (stores.value ?? [])
        .map((store) => store?.id)
        .filter((id): id is string => Boolean(id))
      const mockIds = allIds.filter((id) => testDataStoreIds.value.includes(id))
      const realIds = allIds.filter(
        (id) => !testDataStoreIds.value.includes(id),
      )

      let response: ProfileMetricsResponse = { ids: [], metrics: {} }

      // Skip the live API when every store is mocked or the caller opts out
      // (e.g. no store can view the new metrics).
      if (realIds.length && !options.skipLiveFetch?.()) {
        response = (await ufetch('/api/new-profile-metrics', {
          method: 'POST',
          body: { roles: roles?.value },
        })) as ProfileMetricsResponse
      }

      // Overlay mock data for allowlisted stores.
      if (mockIds.length) {
        const mock = generateTestProfileMetricsResponse(mockIds, { version: 2 })
        response = {
          ids: Array.from(new Set([...response.ids, ...mock.ids])),
          metrics: { ...response.metrics, ...mock.metrics },
        }
      }

      return response
    },
    {
      transform(input) {
        return {
          ...input,
          fetchedAt: useUnixTimestamp(),
        }
      },
      getCachedData: (key) => useCachedData(key),
    },
  )

  return data
}
